import * as React from 'react'
import { RefObject, useEffect, useRef, useState } from 'react'
import { Alert, Grid, Box } from '@mui/material'
import { useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { faSave } from '@fortawesome/pro-duotone-svg-icons/faSave'
import { faTimesCircle } from '@fortawesome/pro-duotone-svg-icons/faTimesCircle'
import { faChevronLeft } from '@fortawesome/pro-duotone-svg-icons/faChevronLeft'

import { toast, ToastContainer } from 'react-toastify'
import { ToastOptions } from 'react-toastify/dist/types'
import BagHelper from '../../../util/BagHelper'
import DefaultGrid from '@/feature/Grid/DefaultGrid'
import ShowMultiAdd from '../NewContentDossier/Components/ShowMultiAdd'
import LoadingPage from '../../../Components/Elements/Loading/LoadingPage'
import LoadingDefault from '../../../Components/Elements/Loading/LoadingDefault'
import EntityHelper from '../../../util/EntityHelper'
import {
    IDisplayPositionList,
    useEntityService,
} from '../../../context/EntityServiceContext'
import DisplayItemHelper from '../../../util/DisplayItemHelper'
import DetailContentDossier from '../Detail/ContentDossier'
import Kedo from '../../../Kedo.js'
import Sidebar from '../../../Components/Elements/Sidebar/Sidebar'
import {
    ConfirmationModal,
    useUnsavedChangesModal,
} from '../../../Components/Elements/UnsavedChangesModal'
import AppDialog from '@/Components/AppDialog/AppDialog'
import { StyledButton } from '@/Components/Elements/StyledComponents/Buttons'

type Props = {
    kedo: Kedo
    props: any
}

const CreateContentDossier = ({ kedo, props }: Props): React.JSX.Element => {
    const toastOptions = {
        position: 'bottom-center',
        autoClose: 2500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
    } as ToastOptions
    const params = useParams()
    const [defDossier, setDefDossier] = useState(null)
    const { showModal, isVisible, modalConfig, setBlockLocation } =
        useUnsavedChangesModal({
            title: kedo.t('Confirmation'),
            content: kedo.t('You made unsaved changes'),
            confirmBtnLabel: kedo.t('Leave page'),
            closeBtnLabel: kedo.t('Continue working'),
            onConfirm: () => props.history?.goBack(),
        })
    const entityService = useEntityService()

    const [loading, setLoading] = useState(false)
    const [showSaveSuccess, setShowSaveSuccess] = useState(false)
    const [denied_error, setDenied_error] = useState(false)
    const [showExisting, setShowExisting] = useState([])

    const [existingConstraintType, setExistingConstraintType] = useState('')
    const [ignoreWarning, setIgnoreWarning] = useState(false)
    const [embedded, setEmbedded] = useState(false)
    const [saved, setSaved] = useState(false)
    const [changed, setChanged] = useState({})
    const [submitting, setSubmitting] = useState(false)
    const [redirect, setRedirect] = useState(false)
    const [edit, setEdit] = useState(true)
    const [content, setContent] = useState(
        props && props.content ? props.content : {}
    )
    const [mainPos, setMainPos] = useState({})
    const [errors, setErrors] = useState({})
    const [items, setItems] = useState([])

    const [values, setValues] = useState([])
    const [showExistingUserModal, setShowExistingUserModal] = useState(false)
    const [existingUser, setExistingUser] = useState(null)
    const [id, setId] = useState(
        props && props.defDossierId ? props.defDossierId : params.id
    )
    const refs = useRef([])
    let curRef = useRef(null)
    let dossierRef = useRef(null)

    const [showMultiAddModal, setShowMultiAddModal] = useState(false)
    const showMultiAddModalRef = useRef(showMultiAddModal)

    useEffect(() => {
        showMultiAddModalRef.current = showMultiAddModal
    }, [showMultiAddModal])

    const preSubmitDossier = () => {
        if (props.multiAdd) {
            setShowMultiAddModal(true)
            return
        }
        submitDossier(false)
    }

    const renderMultiAdd = () => {
        //TODO: Add check
        return (
            <ShowMultiAdd
                defDossier={defDossier}
                kedo={kedo}
                displayItems={items}
                handleGetValues={() => getCurrentRef().current.getValues()}
                handleSuccess={props.onSuccess}
                handleClose={() => setShowMultiAddModal(false)}
                linkId={props.linkId}
                embedded={props.embedded}
            />
        )
    }

    const fetchDisplayPositions = async (defDossierId) => {
        const dpUrl =
            kedo.api().getDisplayPositionEndpoint() +
            '?defDossier=' +
            defDossierId
        return await kedo.api().getCached(dpUrl)
    }

    const fetchDisplayItems = async () => {
        const ddiParams = {
            params: {
                view: ['edit', 'show/edit'],
                defDossier: id,
                displayPosition: ['main_content'],
                sort: ['rank', 'col'],
                sortOrder: ['ASC', 'ASC'],
            },
        }

        setLoading(true)

        const ddPositions = (await entityService.getDisplayPositions(
            id
        )) as IDisplayPositionList

        const entity = (await entityService.getEntity(id)) as Entity

        const mainPos = ddPositions.results.find(
            (dpItem) => dpItem.type === 'main_content'
        )

        setDefDossier(entity)
        setMainPos(mainPos)

        const roleIds = kedo
            .env()
            .getCurrentEnvironmentRoles()
            .map((item) => item.id)

        setItems(
            DisplayItemHelper.filterViews(mainPos.display_items, [
                DisplayItemHelper.EDIT_VIEW,
                DisplayItemHelper.SHOW_EDIT_VIEW,
            ]).filter((item) => {
                if (
                    !item.permissions_type ||
                    kedo.isAdminOrEnvironmentAdmin()
                ) {
                    return true
                }

                if (item.permissions_type === 'INCLUDE_SELECTED_ROLES') {
                    if (
                        item.roles.filter((roleItem) =>
                            roleIds.includes(roleItem.role.id)
                        ).length > 0
                    ) {
                        return true
                    } else {
                        return false
                    }
                } else if (item.permissions_type === 'EXCLUDE_SELECTED_ROLES') {
                    if (
                        item.roles.filter((roleItem) =>
                            roleIds.includes(roleItem.role.id)
                        ).length <= 0
                    ) {
                        return true
                    } else {
                        return false
                    }
                }

                return true
            })
        )
        setLoading(false)
    }

    const getCurrentRef = (): RefObject<any> => {
        if (!curRef) {
            curRef = React.createRef()
        }

        return curRef
    }

    const getDossierRef = (): RefObject<any> => {
        if (!dossierRef) {
            dossierRef = React.createRef()
        }

        return dossierRef
    }

    const findAddress = (displayItem, changedDdiId) => {
        BagHelper.autocomplete(kedo, defDossier, getCurrentRef())
    }

    const autoFillCompany = (
        settings: Record<string, string>,
        value: any
    ): void => {
        const currentRef = getCurrentRef().current

        for (const [key, setting] of Object.entries(settings)) {
            const fieldRef = currentRef[`ddiRef${setting}`]?.current?.ddiRef
            if (!fieldRef) continue

            if (!value) {
                fieldRef.current.setVal('value', '')
                continue
            }

            const { adres = {}, geoData = {} } = value
            const { binnenlandsAdres = {} } = adres

            let fieldValue: any = value[key]

            switch (key) {
                case 'adres_type':
                    fieldValue = binnenlandsAdres?.type
                    break
                case 'plaats':
                case 'straatnaam':
                    fieldValue = binnenlandsAdres?.[key]
                    break
                case 'location':
                    fieldRef.current.setVal(
                        'latitude',
                        geoData?.gpsLatitude ?? ''
                    )
                    fieldRef.current.setVal(
                        'longitude',
                        geoData?.gpsLongitude ?? ''
                    )
                    continue
                default:
                    break
            }

            fieldRef.current.setVal(
                'value',
                fieldValue !== undefined ? fieldValue : ''
            )
        }
    }

    const onChangeValue = (itemId, value, item) => {
        if (defDossier?.settings?.address_company_settings?.company_lookup) {
            autoFillCompany(defDossier.settings.address_company_settings, value)
            return
        }

        const newErrors = { ...errors }
        if (newErrors[itemId]) {
            delete newErrors[itemId]
            setErrors(newErrors)
        }

        //BAG: Lookup addresses
        if (
            defDossier &&
            defDossier.settings &&
            defDossier.settings.address_lookup_settings &&
            defDossier.settings.address_lookup_settings.address_lookup === true
        ) {
            if (
                parseInt(
                    defDossier.settings.address_lookup_settings.zipcode_ddi
                ) === itemId
            ) {
                findAddress(item, itemId)
            } else if (
                parseInt(
                    defDossier.settings.address_lookup_settings.housenr_ddi
                ) === itemId
            ) {
                findAddress(item, itemId)
            }
        }

        setValues(value)

        //TODO KeDo: Check conditions here.
        //TODO KeDo: Check other modules here and maybe change data based on
    }

    const handleUnsavedChanges = (href) => {
        showModal(
            () => {
                if (!props.embedded && href) {
                    props.history.push(href)
                } else if (
                    props.location === 'Block' ||
                    props.location === 'EmbeddedLinkField'
                ) {
                    props?.onClose()
                } else {
                    props.history?.goBack()
                }
            },
            () => {
                if (props.defDossierId && !props.embedded) {
                    props?.history?.goBack()
                }
            }
        )
    }

    const submitDossier = (addContinue): any => {
        setSubmitting(true)

        const values = getCurrentRef().current.getValues()
        if (props.embedded && props.embedded !== true) {
            values['embedded'] = props.embedded
            values['linkId'] = props.linkId
        }

        if (ignoreWarning === true) {
            values['ignore_existing'] = true
        }

        values.defDossier = id

        const submitErrors = EntityHelper.checkCanSubmitDisplayPosition(
            kedo,
            defDossier,
            mainPos,
            getCurrentRef(),
            values,
            ignoreWarning
        )

        if (Object.keys(submitErrors).length > 0) {
            setErrors(submitErrors)
            setSubmitting(false)
            const errorId = checkErrors(submitErrors)
            getCurrentRef().current.doFocus(errorId)
            toast.error(kedo.t('not_all_fields_filled'), toastOptions)
            return
        }

        kedo.api()
            .post(kedo.api().getContentDossierEndpoint(), values)
            .then((response) => {
                if (addContinue) {
                    setEdit(false)
                    setSubmitting(false)
                    setErrors([])
                    setContent({})
                    setChanged({})
                    setSaved(true)
                    setTimeout(() => setSaved(false), 5000)
                    setTimeout(() => fetchDisplayItems(), 500)
                } else {
                    setRedirect(
                        props.location === 'Block' ? false : response.data?.id
                    )
                    setEdit(false)
                    setSubmitting(false)
                    setErrors([])
                }

                toast.success(
                    kedo.t('dossier_errors.success_create', {
                        defdossier: kedo
                            .env()
                            .translateItem(defDossier, 'defdossier'),
                    }),
                    toastOptions
                )

                if (
                    props.location === 'Block' ||
                    (props.location === 'EmbeddedLinkField' && !addContinue)
                ) {
                    props.onSuccess(response.data.id)
                }
            })
            .catch((error) => {
                if (error.response && error.response.status === 409) {
                    setErrors(error.response.data.errors)
                    setSubmitting(false)
                    if (
                        error.response.data.existing &&
                        error.response.data.existing.length > 0
                    ) {
                        setShowExisting(error.response.data.existing)
                        setExistingConstraintType(
                            error.response.data.constraint_type
                        )
                    }
                } else if (error.response && error.response.status === 403) {
                    setDenied_error(kedo.t('not_allowed_to_create_dossier'))
                    setSubmitting(false)

                    toast.error(
                        kedo.t('dossier_errors.permission_create'),
                        toastOptions
                    )
                    window.scrollTo(0, 0)
                } else {
                    toast.error(
                        kedo.t('dossier_errors.error_create'),
                        toastOptions
                    )
                    setErrors(error.response.data.errors)
                    setSubmitting(false)
                    const errorId = checkErrors(error.response.data.errors)
                    getCurrentRef().current.doFocus(errorId)
                }
            })
    }

    const getAllValues = () => {
        const values = getCurrentRef().current.getValues()
        if (props.embedded && props.embedded !== true) {
            values['embedded'] = props.embedded
            values['linkId'] = props.linkId
        }

        return values
    }

    const renderButtons = () => {
        const addContinue =
            defDossier &&
            defDossier.settings &&
            defDossier.settings.add_continue === true
                ? true
                : false
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <StyledButton
                    disabled={submitting ? true : false}
                    type="submit"
                    onClick={() => preSubmitDossier()}
                    title="Create"
                >
                    {submitting ? (
                        <LoadingDefault size={'sm'} as={'span'} />
                    ) : (
                        <FontAwesomeIcon icon={faSave} />
                    )}
                    &nbsp; {kedo.t('Add')}
                </StyledButton>{' '}
                {addContinue ? (
                    <StyledButton
                        disabled={submitting ? true : false}
                        type="submit"
                        onClick={() => submitDossier(true)}
                        title="Create"
                    >
                        {submitting ? (
                            <LoadingDefault size={'sm'} as={'span'} />
                        ) : (
                            <FontAwesomeIcon icon={faSave} />
                        )}
                        &nbsp; {kedo.t('Add and continue')}
                    </StyledButton>
                ) : null}
                {props.location === 'Block' ||
                props.location === 'EmbeddedLinkField' ? (
                    <StyledButton
                        color="secondary"
                        title={kedo.t('Cancel')}
                        onClick={() => {
                            if (values?.length > 0) {
                                handleUnsavedChanges(null)
                            } else {
                                props.onClose()
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faTimesCircle} />
                        &nbsp; {kedo.t('Cancel')}
                    </StyledButton>
                ) : (
                    <StyledButton
                        color="secondary"
                        title={kedo.t('Cancel')}
                        onClick={() => {
                            if (values?.length > 0) {
                                handleUnsavedChanges(
                                    `/defdossier/${defDossier.id}`
                                )
                            } else {
                                props.history.goBack()
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faTimesCircle} />
                        &nbsp; {kedo.t('Cancel')}
                    </StyledButton>
                )}
            </Box>
        )
    }

    const renderSuccessAlert = () => {
        return (
            <Alert key={'success'} severity={'success'}>
                &nbsp; {kedo.t('Dossier has been saved successfully')}
            </Alert>
        )
    }

    const checkErrors = (errors) => {
        const values = []
        const newItems = []
        items
            .filter((item) => item.view === 'edit' || item.view === 'show/edit')
            .map((item) => newItems.push(item))

        newItems.map((filterItem) => {
            values.push({
                id: filterItem.id,
                type: filterItem.def_dossier_def_field
                    ? filterItem.def_dossier_def_field.def_field.type
                    : filterItem.def_dossier_link,
                error: errors ? errors[filterItem.id] : errors[filterItem.id],
            })
        })

        const focusItem = values.find((item) => item.error)

        if (focusItem) {
            return focusItem.id
        }
    }

    const handleClick = (e) => {
        if (showMultiAddModalRef.current) return
        const target = e.target as HTMLElement

        // Ignore clicks inside rc-select (spans break target checks)
        if (target.closest('.rc-select')) return

        if (!getDossierRef()?.current.contains(e.target)) {
            if (
                e.target.tagName.toLowerCase() === 'a' ||
                e.target.parentNode.tagName.toLowerCase() === 'a'
            ) {
                e.preventDefault()
                const href = e.target.closest('a').getAttribute('href')
                values?.length > 0 && href !== '#'
                    ? handleUnsavedChanges(href)
                    : null
            }
        }
    }

    useEffect(() => {
        fetchDisplayItems()
    }, [])

    useEffect(() => {
        document.addEventListener('mousedown', handleClick)
        setBlockLocation(values?.length > 0)
        return () => {
            document.removeEventListener('mousedown', handleClick)
        }
    }, [values])

    const renderExistingUser = () => {
        return (
            <AppDialog
                aria-labelledby="contained-modal-title-vcenter"
                open
                maxWidth={'lg'}
                fullWidth
                title={existingUser.summary}
                onClose={() => setShowExistingUserModal(false)}
                headerClose={() => setShowExistingUserModal(false)}
                contentProps={{ dividers: true }}
                actions={
                    <>
                        {existingConstraintType === 'warning' ? (
                            <StyledButton
                                onClick={() => {
                                    setIgnoreWarning(true)
                                    setTimeout(
                                        () =>
                                            props.onSuccess
                                                ? props.onSuccess(
                                                      existingUser.id
                                                  )
                                                : props.history.goBack(),
                                        50
                                    )
                                }}
                            >
                                {kedo.t('Use this item', {
                                    item: kedo
                                        .env()
                                        .translateItem(
                                            defDossier,
                                            'defdossier'
                                        ),
                                })}
                            </StyledButton>
                        ) : null}
                        <StyledButton
                            onClick={() => setShowExistingUserModal(false)}
                            color={'secondary'}
                        >
                            {kedo.t('Close')}
                        </StyledButton>
                    </>
                }
            >
                <DetailContentDossier
                    props={{
                        existingUser: true,
                        modalized: true,
                        dossierId: existingUser.id,
                        disableBreadcrumb: true,
                    }}
                    kedo={kedo}
                />
            </AppDialog>
        )
    }

    const renderExisting = () => {
        return (
            <>
                <AppDialog
                    aria-labelledby="contained-modal-title-vcenter"
                    open
                    maxWidth={'lg'}
                    fullWidth
                    title={kedo.t('Already existing items')}
                    onClose={() => setShowExisting([])}
                    headerClose={() => setShowExisting([])}
                    contentProps={{ dividers: true }}
                    actions={
                        <>
                            {existingConstraintType === 'warning' ? (
                                <StyledButton
                                    onClick={() => {
                                        setIgnoreWarning(true)
                                        setTimeout(
                                            () => submitDossier(false),
                                            50
                                        )
                                    }}
                                >
                                    {kedo.t('Ignore warning and save')}
                                </StyledButton>
                            ) : null}
                            <StyledButton
                                onClick={() => setShowExisting([])}
                                color={'secondary'}
                            >
                                {kedo.t('Close')}
                            </StyledButton>
                        </>
                    }
                >
                    <Box>
                        <p>{kedo.t('existing_items_description')}</p>
                        <ul className={'list-group'}>
                            {showExisting.map((existingItem) => (
                                <li
                                    key={existingItem.id}
                                    className={
                                        'list-group-item list-group-item-action'
                                    }
                                    onClick={() => {
                                        setShowExistingUserModal(true)
                                        setExistingUser(existingItem)
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {existingItem.summary
                                        ? existingItem.summary
                                        : existingItem.id}
                                </li>
                            ))}
                        </ul>
                    </Box>
                </AppDialog>
                {showExistingUserModal !== false ? renderExistingUser() : null}
            </>
        )
    }

    useEffect(() => {
        if (redirect) {
            setBlockLocation(false)
            setTimeout(() => {
                props.history.push(`/contentdossier/${redirect}`)
                setRedirect(false)
            }, 500)
        }
    }, [redirect])

    if (
        !defDossier ||
        loading ||
        (props.match && id !== props.match.params.id)
    ) {
        return <LoadingPage />
    }

    return (
        <div ref={dossierRef}>
            <ToastContainer />
            {!showExistingUserModal && isVisible && (
                <ConfirmationModal
                    title={kedo.t('Discard changes to {{dossierName}}', {
                        dossierName: kedo.translateItem(
                            defDossier ? defDossier : null,
                            'defdossier',
                            false
                        ),
                    })}
                    content={kedo.t('You made unsaved changes')}
                    confirmBtnLabel={kedo.t('Leave page')}
                    closeBtnLabel={kedo.t('Continue working')}
                    onClose={modalConfig.onClose}
                    onConfirm={modalConfig.onConfirm}
                />
            )}
            <Grid container>
                {props.leftSideBar && window.innerWidth > 576 ? (
                    <Grid
                        sx={{
                            borderRight: 1,
                            borderColor: 'divider',
                            bgcolor: 'grey.50',
                        }}
                        size={props.location === 'EmbeddedLinkField' ? 3 : 4}
                    >
                        <Sidebar
                            kedo={kedo}
                            defDossier={defDossier}
                            embeddedDefDossierId={props.embeddedDefDossierId}
                            dossierId={props.embedded}
                            leftSideBar={props.leftSideBar}
                        />
                    </Grid>
                ) : null}
                <Grid
                    size={
                        props.leftSideBar && window.innerWidth > 576
                            ? props.location === 'EmbeddedLinkField'
                                ? 9
                                : 8
                            : 12
                    }
                >
                    {showMultiAddModal ? renderMultiAdd() : null}
                    {showExisting.length > 0 ? renderExisting() : null}
                    {denied_error !== false ? (
                        <Alert severity={'warning'}>{denied_error}</Alert>
                    ) : null}
                    {saved === true ? (
                        <Alert severity={'success'}>
                            {kedo.t('Dossier has been saved successfully')}
                        </Alert>
                    ) : null}
                    <div
                        className={
                            props.location === 'EmbeddedLinkField'
                                ? ''
                                : 'mainContainer'
                        }
                    >
                        {showSaveSuccess === true ? renderSuccessAlert() : null}
                        {props.location === 'EmbeddedLinkField' ? null : (
                            <Grid container>
                                <Grid size={8}>
                                    <h1>
                                        {props.multiAdd ? (
                                            <small>Multi add </small>
                                        ) : null}
                                        {kedo.t('New')}{' '}
                                        {kedo
                                            .env()
                                            .translateItem(
                                                defDossier,
                                                'defdossier'
                                            )}
                                    </h1>
                                </Grid>
                                {items.length ? (
                                    <Grid size={4}>{renderButtons()}</Grid>
                                ) : null}
                            </Grid>
                        )}
                        <Box
                            sx={
                                props.leftSideBar
                                    ? {
                                          '& > .MuiGrid-root': {
                                              mx: '0 !important',
                                          },
                                      }
                                    : undefined
                            }
                        >
                            {props.multiAdd ? (
                                <Alert severity={'info'}>
                                    {kedo.t('multi_add_dossier_header')}
                                </Alert>
                            ) : null}
                            <DefaultGrid
                                changeValue={onChangeValue}
                                conditions={
                                    defDossier?.settings?.conditions
                                        ? defDossier.settings.conditions
                                        : []
                                }
                                content={content}
                                embedded={props.embedded}
                                linkId={props.linkId}
                                errors={errors}
                                hiddenfields={
                                    defDossier?.settings?.hiddenfields
                                        ? defDossier.settings.hiddenfields
                                        : []
                                }
                                items={items}
                                kedo={kedo}
                                mode={'edit'}
                                ref={getCurrentRef()}
                                settings={defDossier.settings}
                            />
                            <Grid container>
                                <Grid size={12}>{renderButtons()}</Grid>
                            </Grid>
                        </Box>
                    </div>
                </Grid>
                {props.defDossierId ? null : (
                    <Grid size={12}>
                        <StyledButton
                            className={'goBackButton'}
                            color={'secondary'}
                            onClick={() => props.history.goBack()}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                            &nbsp; {kedo.t('Back to overview')}
                        </StyledButton>
                    </Grid>
                )}
            </Grid>
        </div>
    )
}

export default CreateContentDossier
